/**
 * Optional local-dev quick-fill logins. Set both EMAIL and PASSWORD for a role
 * in `.env.local` (see VITE_TEST_LOGIN_*). Only active when the Vite dev server
 * targets a non-production environment.
 */

import { isProduction } from "@/lib/appEnvironment";

export type TestLoginRoleId =
  | "salesAgent"
  | "admin"
  | "seller"
  | "host"
  | "customer";

type TestLoginVariant = "violet" | "primary" | "accent" | "emerald" | "sky";

export type TestLoginProfile = {
  roleId: TestLoginRoleId;
  email: string;
  password: string;
  variant: TestLoginVariant;
  /** Wider card in the 2-column grid */
  fullWidth: boolean;
};

function profile(
  roleId: TestLoginRoleId,
  variant: TestLoginVariant,
  fullWidth: boolean,
  email: string | undefined,
  password: string | undefined,
): TestLoginProfile | null {
  const normalizedEmail = email?.trim() ?? "";
  const normalizedPassword = password?.trim() ?? "";
  if (!normalizedEmail || !normalizedPassword) {
    return null;
  }
  return {
    roleId,
    variant,
    fullWidth,
    email: normalizedEmail,
    password: normalizedPassword,
  };
}

export type TestLoginEnv = Pick<
  ImportMetaEnv,
  | "VITE_TEST_LOGIN_ADMIN_EMAIL"
  | "VITE_TEST_LOGIN_ADMIN_PASSWORD"
  | "VITE_TEST_LOGIN_SELLER_EMAIL"
  | "VITE_TEST_LOGIN_SELLER_PASSWORD"
  | "VITE_TEST_LOGIN_HOST_EMAIL"
  | "VITE_TEST_LOGIN_HOST_PASSWORD"
  | "VITE_TEST_LOGIN_SALES_AGENT_EMAIL"
  | "VITE_TEST_LOGIN_SALES_AGENT_PASSWORD"
  | "VITE_TEST_LOGIN_CUSTOMER_EMAIL"
  | "VITE_TEST_LOGIN_CUSTOMER_PASSWORD"
>;

/** Roles that have both email and password set in `env`. */
export function buildTestLoginProfiles(env: TestLoginEnv): TestLoginProfile[] {
  const profiles: Array<TestLoginProfile | null> = [
    profile(
      "admin",
      "primary",
      true,
      env.VITE_TEST_LOGIN_ADMIN_EMAIL,
      env.VITE_TEST_LOGIN_ADMIN_PASSWORD,
    ),
    profile(
      "seller",
      "accent",
      false,
      env.VITE_TEST_LOGIN_SELLER_EMAIL,
      env.VITE_TEST_LOGIN_SELLER_PASSWORD,
    ),
    profile(
      "host",
      "emerald",
      false,
      env.VITE_TEST_LOGIN_HOST_EMAIL,
      env.VITE_TEST_LOGIN_HOST_PASSWORD,
    ),
    profile(
      "salesAgent",
      "violet",
      false,
      env.VITE_TEST_LOGIN_SALES_AGENT_EMAIL,
      env.VITE_TEST_LOGIN_SALES_AGENT_PASSWORD,
    ),
    profile(
      "customer",
      "sky",
      false,
      env.VITE_TEST_LOGIN_CUSTOMER_EMAIL,
      env.VITE_TEST_LOGIN_CUSTOMER_PASSWORD,
    ),
  ];
  return profiles.filter((p): p is TestLoginProfile => p !== null);
}

/** Roles that have both email and password env vars set (local dev only). */
export const configuredTestLoginProfiles: readonly TestLoginProfile[] =
  import.meta.env.DEV && !isProduction
    ? buildTestLoginProfiles(import.meta.env)
    : [];
